import { useState, useRef } from 'react';
import api from '../services/api';

/**
 * Platnosc dzielona - wiersze adresowane po `id` (nie po `method`), bo kuponow
 * (kart podarunkowych) moze byc WIELE na jednym paragonie (kontrakt K5,
 * 2026-09-11). Gotowka/karta/blik maja id === method (sa pojedyncze),
 * kupony maja id `kupon-<n>` i kazdy wlasny couponCode + amount.
 *
 * Zgodnosc wsteczna: updateSplitPaymentAmount('kupon', x) trafia w PIERWSZY
 * wiersz kuponu, a updateSplitPaymentCouponCode('KOD') (jeden argument)
 * rowniez - stare wywolania dzialaja bez zmian.
 */

const STALE_WIERSZE = () => ([
  { id: 'gotowka', method: 'gotowka', amount: 0, label: 'Gotowka', icon: 'fas fa-money-bill-wave', color: 'var(--success)' },
  { id: 'karta', method: 'karta', amount: 0, label: 'Karta', icon: 'fas fa-credit-card', color: 'var(--info)' },
  { id: 'blik', method: 'blik', amount: 0, label: 'BLIK', icon: 'fas fa-mobile-alt', color: 'var(--orange)' },
]);

const nowyWierszKuponu = (nr) => ({
  id: `kupon-${nr}`,
  method: 'kupon',
  amount: 0,
  label: 'Kupon',
  icon: 'fas fa-ticket-alt',
  color: 'var(--purple)',
  couponCode: '',
  // Saldo z ostatniej walidacji kodu (null = jeszcze nie sprawdzony)
  balance: null,
  balanceError: '',
});

const poczatkoweWiersze = () => [...STALE_WIERSZE(), nowyWierszKuponu(1)];

const kwotaPL = (n) => (Number(n) || 0).toFixed(2).replace('.', ',');

const normalizujKod = (code) => (typeof code === 'string' ? code.trim().toUpperCase() : '');

/**
 * Znajdz wiersz po id; gdy nie ma takiego id - po metodzie (pierwszy
 * pasujacy). Dzieki temu 'kupon' oznacza pierwszy wiersz kuponu.
 */
const znajdzWiersz = (rows, key) =>
  rows.find(r => r.id === key) || rows.find(r => r.method === key) || null;

const useSplitPayment = () => {
  const [showSplitPaymentModal, setShowSplitPaymentModalInternal] = useState(false);
  const [splitPayments, setSplitPayments] = useState(poczatkoweWiersze);
  const [splitPaymentError, setSplitPaymentError] = useState('');
  // Licznik id kolejnych wierszy kuponu (1 zajete przez wiersz startowy)
  const kuponSeq = useRef(1);

  // Wrapper dla setShowSplitPaymentModal - czysci blad przy otwieraniu modalu
  const setShowSplitPaymentModal = (value) => {
    if (value === true) {
      setSplitPaymentError('');
    }
    setShowSplitPaymentModalInternal(value);
  };

  const resetSplitPayments = () => {
    kuponSeq.current = 1;
    setSplitPayments(poczatkoweWiersze());
    setSplitPaymentError('');
  };

  const updateSplitPaymentAmount = (key, amount) => {
    const roundedAmount = Math.round((parseFloat(amount) || 0) * 100) / 100;
    setSplitPayments(prev => {
      const cel = znajdzWiersz(prev, key);
      if (!cel) return prev;
      return prev.map(payment =>
        payment.id === cel.id ? { ...payment, amount: roundedAmount } : payment
      );
    });
    // Czysc blad przy zmianie kwoty - uzytkownik probuje poprawic
    setSplitPaymentError('');
  };

  /**
   * updateSplitPaymentCouponCode(id, code) - kod dla konkretnego wiersza.
   * updateSplitPaymentCouponCode(code)     - kod dla pierwszego kuponu (stara sygnatura).
   */
  const updateSplitPaymentCouponCode = (keyOrCode, maybeCode) => {
    const key = maybeCode === undefined ? 'kupon' : keyOrCode;
    const code = maybeCode === undefined ? keyOrCode : maybeCode;
    // Kody kuponow sa kanonicznie wielkimi literami (tak generuje je serwer)
    const znormalizowany = typeof code === 'string' ? code.toUpperCase() : code;
    setSplitPayments(prev => {
      const cel = znajdzWiersz(prev, key);
      if (!cel || cel.method !== 'kupon') return prev;
      return prev.map(payment =>
        payment.id === cel.id
          // Zmiana kodu uniewaznia poprzednie saldo
          ? { ...payment, couponCode: znormalizowany, balance: null, balanceError: '' }
          : payment
      );
    });
    // Czysc blad przy zmianie kodu kuponu
    setSplitPaymentError('');
  };

  /** Dodaj kolejny wiersz kuponu (druga, trzecia karta...). Zwraca jego id. */
  const addCouponRow = () => {
    kuponSeq.current += 1;
    const nowy = nowyWierszKuponu(kuponSeq.current);
    setSplitPayments(prev => [...prev, nowy]);
    setSplitPaymentError('');
    return nowy.id;
  };

  /**
   * Usun wiersz kuponu. Gdy to JEDYNY wiersz kuponu - nie znika, tylko
   * zostaje wyczyszczony (zawsze jest gdzie wpisac karte).
   */
  const removeCouponRow = (key) => {
    setSplitPayments(prev => {
      const cel = znajdzWiersz(prev, key);
      if (!cel || cel.method !== 'kupon') return prev;
      const kupony = prev.filter(p => p.method === 'kupon');
      if (kupony.length <= 1) {
        return prev.map(p => (p.id === cel.id ? { ...nowyWierszKuponu(0), id: cel.id } : p));
      }
      return prev.filter(p => p.id !== cel.id);
    });
    setSplitPaymentError('');
  };

  const getTotalSplitAmount = () => {
    return splitPayments.reduce((total, payment) => total + (payment.amount || 0), 0);
  };

  /**
   * Odpowiedz walidatora -> { ok, value } albo { ok: false, message }.
   * Jezyk kasjerki: zuzyty kupon to najczesciej karta rozladowana przed
   * chwila na innej kasie (W1/W4).
   */
  const sprawdzKodNaSerwerze = async (kod) => {
    try {
      const response = await api.get(`/coupons/validate/${normalizujKod(kod)}`);
      const data = response.data;
      if (data.success && data.data && data.data.valid) {
        return { ok: true, value: Number(data.data.value) || 0, couponData: data.data };
      }
      const errors = data.data?.errors || [];
      let errorMessage = errors.length > 0 ? errors.join(', ') : (data.message || 'Kupon nie jest wazny');
      if (errorMessage.indexOf('wykorzystany') !== -1) {
        errorMessage += ' - kupon zostal juz zuzyty (byc moze przed chwila na innej kasie). Przyjmij te czesc zaplaty inna metoda.';
      }
      return { ok: false, message: `Blad kuponu: ${errorMessage}` };
    } catch (error) {
      return { ok: false, message: `Blad walidacji kuponu: ${error.message}` };
    }
  };

  const ustawSaldoWiersza = (id, balance, balanceError) => {
    setSplitPayments(prev => prev.map(p => (p.id === id ? { ...p, balance, balanceError } : p)));
  };

  /**
   * Sprawdz saldo JEDNEGO wiersza kuponu (np. po zeskanowaniu karty) - wynik
   * laduje w wierszu (balance / balanceError), zeby kasjerka widziala saldo
   * zanim wpisze kwote.
   */
  const checkCouponRow = async (key) => {
    const cel = znajdzWiersz(splitPayments, key);
    if (!cel || cel.method !== 'kupon') return { ok: false };
    const kod = normalizujKod(cel.couponCode);
    if (!kod) return { ok: false };
    const wynik = await sprawdzKodNaSerwerze(kod);
    if (wynik.ok) {
      ustawSaldoWiersza(cel.id, wynik.value, '');
    } else {
      ustawSaldoWiersza(cel.id, null, wynik.message);
    }
    return wynik;
  };

  const validateSplitPayments = async (totalAmount) => {
    const splitTotal = getTotalSplitAmount();

    // ROWNOSC CO DO GROSZA - ta sama regula co money_equal na serwerze.
    // Dawna tolerancja 0.01 przepuszczala split rozny o grosz, ktory serwer
    // odrzucal (PLATNOSC_NIEZGODNA) - modal nie moze przepuscic rozbicia,
    // ktorego finalizacja nie przyjmie (kwota pobrana od klienta i kwota na
    // paragonie maja byc TA SAMA liczba - ustalenie A wlasciciela).
    if (Math.round(splitTotal * 100) !== Math.round(totalAmount * 100)) {
      setSplitPaymentError(`Suma platnosci (${splitTotal.toFixed(2)} zl) nie rowna sie calkowitej kwocie (${totalAmount.toFixed(2)} zl). Roznica: ${Math.abs(splitTotal - totalAmount).toFixed(2)} zl`);
      return { valid: false };
    }

    // Kupony z kwota > 0 - KAZDY sprawdzany osobno (K5: wiele kart)
    const kupony = splitPayments.filter(p => p.method === 'kupon' && p.amount > 0);
    if (kupony.length === 0) {
      return { valid: true };
    }

    // W3: bez kodu nie ma platnosci kuponem - serwer i tak odmowi (400),
    // wiec zatrzymujemy kasjerke juz w oknie platnosci dzielonej.
    const bezKodu = kupony.find(p => !p.couponCode || p.couponCode.trim() === '');
    if (bezKodu) {
      const ktory = kupony.length > 1 ? ` (kupon ${kupony.indexOf(bezKodu) + 1})` : '';
      setSplitPaymentError(`Podaj kod kuponu${ktory} (zeskanuj karte albo wpisz kod), inaczej platnosc kuponem nie przejdzie.`);
      return { valid: false };
    }

    // Ten sam kod dwa razy - serwer zsumowalby wymaganie, ale kasjerka
    // najpewniej zeskanowala te sama karte dwa razy.
    const widziane = new Set();
    for (const p of kupony) {
      const kod = normalizujKod(p.couponCode);
      if (widziane.has(kod)) {
        setSplitPaymentError(`Ten sam kupon podano dwa razy (${kod}). Kazda karte podaj raz - jesli klient placi jedna karta, wpisz cala kwote w jednym wierszu.`);
        return { valid: false };
      }
      widziane.add(kod);
    }

    const couponsData = [];
    for (const p of kupony) {
      const kod = normalizujKod(p.couponCode);
      const wynik = await sprawdzKodNaSerwerze(kod);
      if (!wynik.ok) {
        ustawSaldoWiersza(p.id, null, wynik.message);
        setSplitPaymentError(kupony.length > 1 ? `Kupon ${kod}: ${wynik.message}` : wynik.message);
        return { valid: false };
      }
      ustawSaldoWiersza(p.id, wynik.value, '');
      if (wynik.value < p.amount) {
        setSplitPaymentError(`Kupon ${kod}: saldo ${kwotaPL(wynik.value)} zl, wpisano ${kwotaPL(p.amount)} zl. Zmniejsz kwote na tym kuponie albo dodaj kolejna karte.`);
        return { valid: false };
      }
      couponsData.push({ ...wynik.couponData, code: kod, amount: p.amount, rowId: p.id });
    }

    return { valid: true, couponData: couponsData[0], couponsData };
  };

  return {
    showSplitPaymentModal,
    setShowSplitPaymentModal,
    splitPayments,
    setSplitPayments,
    splitPaymentError,
    setSplitPaymentError,
    resetSplitPayments,
    updateSplitPaymentAmount,
    updateSplitPaymentCouponCode,
    addCouponRow,
    removeCouponRow,
    checkCouponRow,
    getTotalSplitAmount,
    validateSplitPayments
  };
};

export default useSplitPayment;
